import React from 'react';
import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Router from 'next/router';
import { Button } from '@/components/shadcn/button';
import { IRegion, IStats } from '@/types/explorer';
import { Input } from '@/components/shadcn/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import {
  formatNumber,
  calculateCoverageArea,
  getMultiplierFactor,
  getRoundPercent,
  getTotalKm,
} from '@/utils/explorer';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import CircleChart from '@/components/ui/charts/CircleChart';
import { colors } from '@/utils/styles';
import Contribute from '@/components/ui/shared/Contribute';
import SearchIcon from '@/components/ui/icons/SearchIcon';

const columns: ColumnDef<IRegion>[] = [
  {
    accessorKey: 'metadata',
    header: '',
    cell: ({ row }) => {
      const metadata: any = row.getValue('metadata');
      const countries: any[] = metadata?.countries || [];
      const flags = countries.map(country => country.flag);
      return <div className="text-base leading-none">{flags}</div>;
    },
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const metadata: any = row.getValue('metadata');
      const countries: any[] = metadata?.countries || [];
      const name: string = row.getValue('name');
      return (
        <div className="flex flex-col gap-0 overflow-hidden md:gap-1">
          <div className="truncate text-xs">{name}</div>
          <div className="text-[10px] text-neutral-600">
            {countries.map(c => c.name).join(', ')}
          </div>
        </div>
      );
    },
    filterFn: (row, columnId, filterValue) => {
      const metadata: any = row.getValue('metadata');
      const countries: any[] = metadata?.countries || [];
      const name: string = row.getValue(columnId);
      const countryNames = countries.map(c => c.name).join(', ');

      if (!filterValue) return true;

      return (
        name.toLowerCase().includes(filterValue.toLowerCase()) ||
        countryNames.toLowerCase().includes(filterValue.toLowerCase())
      );
    },
  },
  {
    accessorKey: 'uniqueCoveredL12Cells',
    header: 'Unique Road Km Mapped',
    cell: ({ row }) => {
      return (
        <div className="text-xs md:text-sm">
          {formatNumber(getTotalKm(row.getValue('uniqueCoveredL12Cells')))}
        </div>
      );
    },
  },
  {
    accessorKey: 'multiplierFactor',
    header: 'Multiplier',
    cell: ({ row }) => {
      const multiplier = getMultiplierFactor(
        getRoundPercent(Number(row.getValue('multiplierFactor'))),
      );
      return <div className=" text-xs md:text-sm">{multiplier}</div>;
    },
  },
];

interface Props {
  regions: IRegion[];
  stats?: IStats;
}

const PAGE_SIZE = 100;

function Regions(props: Props) {
  const { regions = [] } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  regions.sort((a, b) => (a.overallProgress > b.overallProgress ? -1 : 1));
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: regions,
    columns: columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE,
      },
    },
  });

  if (regions.length === 0) return null;

  const {
    europe: europeanData,
    eastAsia: eastAsianData,
    canada: canadaData,
    usa: usaData,
  } = calculateCoverageArea(regions);

  const allRows = table.getCoreRowModel().rows.length;
  const filteredRows = table.getFilteredRowModel().rows.length;
  const isFiltered = allRows !== filteredRows;
  const pageIndex = table.getState().pagination.pageIndex;

  const from = pageIndex * PAGE_SIZE + 1;

  const to =
    from +
    Math.min((pageIndex + 1) * PAGE_SIZE, table.getRowModel().rows.length - 1);

  const total = isFiltered ? filteredRows : allRows;

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <div className="flex h-1/2 w-full flex-col gap-2 px-4 py-2 md:gap-4 md:p-4 lg:h-full lg:w-1/2">
        <div className="relative flex max-w-xs items-center gap-2 rounded border border-neutral-700 bg-neutral-900 px-4 text-neutral-0">
          <SearchIcon className="size-4 fill-current" />
          <Input
            placeholder="Search for a region by name"
            value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
            onChange={event => {
              table.getColumn('name')?.setFilterValue(event.target.value);
              table.setPageIndex(0);
            }}
            className="border-none p-0 leading-none placeholder:text-neutral-700"
          />
        </div>
        <div className="no-scrollbar h-full flex-1 overflow-scroll rounded-md border [&>div]:overflow-visible">
          <Table className="table-fixed">
            <TableHeader className="sticky top-0 bg-neutral-800">
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    return (
                      <TableHead
                        key={header.id}
                        data-order={index}
                        onClick={header.column.getToggleSortingHandler()}
                        className="h-4 w-1/4 p-1.5 leading-tight data-[order='0']:w-[25px] data-[order='1']:w-1/3 data-[order='2']:w-1/3 data-[order='3']:w-1/4 md:h-12 md:px-4 md:data-[order='1']:w-1/2 md:data-[order='2']:w-1/3 md:data-[order='3']:w-1/4">
                        <div className="flex cursor-pointer items-center gap-1 px-0 text-[10px] md:text-sm">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getIsSorted() === 'asc' ? (
                            <CaretUpIcon />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <CaretDownIcon />
                          ) : null}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer leading-none hover:bg-neutral-900"
                    onClick={() =>
                      Router.push(
                        `/network/regions/${row.getValue('name')}/metrics`,
                      )
                    }
                    data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="p-2 md:px-4 md:py-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="p-2 text-xs md:px-4 md:py-3 md:text-sm">
                    No regions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex w-full items-center justify-end space-x-2 text-neutral-0">
          <div className="flex-1 text-[10px] md:text-xs">
            {total > PAGE_SIZE
              ? `Showing ${from} to ${to} of ${total} regions`
              : `Showing ${total} region${total === 1 ? '' : 's'}`}
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              className="text-[10px] md:text-xs"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-[10px] md:text-xs"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}>
              Next
            </Button>
          </div>
        </div>
      </div>
      <div className="no-scrollbar flex w-full flex-col gap-6 overflow-visible bg-neutral-700 p-4 text-neutral-0 lg:w-1/2 lg:overflow-auto">
        <div className="flex flex-1 flex-col gap-2 md:gap-4">
          <h2 className="text-sm font-bold md:text-xl">All-Time Coverage</h2>
          <div className="grid grid-cols-2 gap-2 md:gap-4">
            <Card className="flex-1 rounded-lg bg-neutral-900">
              <CardHeader className="px-3 pb-0 sm:px-4">
                <CardTitle>USA</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-center px-0 py-1 md:py-3">
                <CircleChart
                  className="h-[120px] sm:h-[200px]"
                  data={[
                    {
                      fill: colors.azure[400],
                      value: usaData,
                      name: 'USA',
                    },
                  ]}
                />
              </CardContent>
            </Card>
            <Card className="flex-1 rounded-lg bg-neutral-900">
              <CardHeader className="px-3 pb-0 sm:px-4">
                <CardTitle>Canada</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-center px-0 py-1 md:py-4">
                <CircleChart
                  className="h-[120px] sm:h-[200px]"
                  data={[
                    {
                      fill: colors.azure[400],
                      value: canadaData,
                      name: 'Canada',
                    },
                  ]}
                />
              </CardContent>
            </Card>
            <Card className="flex-1 rounded-lg bg-neutral-900">
              <CardHeader className="px-3 pb-0 sm:px-4">
                <CardTitle>Europe</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-center px-0 py-1 md:py-4">
                <CircleChart
                  className="h-[120px] sm:h-[200px]"
                  data={[
                    {
                      fill: colors.azure[400],
                      value: europeanData,
                      name: 'Europe',
                    },
                  ]}
                />
              </CardContent>
            </Card>
            <Card className="flex-1 rounded-lg bg-neutral-900">
              <CardHeader className="px-3 pb-0 sm:px-4">
                <CardTitle>East Asia</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-center px-0 py-1 md:py-4">
                <CircleChart
                  className="h-[120px] sm:h-[200px]"
                  data={[
                    {
                      fill: colors.azure[400],
                      value: eastAsianData,
                      name: 'East Asia',
                    },
                  ]}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        <Contribute />
      </div>
    </div>
  );
}

export default Regions;
